import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  Chip,
  Paper,
  Divider,
  Skeleton,
  Menu,
  MenuItem,
} from "@mui/material";
import {
  Add as AddIcon,
  Memory as MemoryIcon,
  Speed as SpeedIcon,
  Bolt as BoltIcon,
  Cloud as CloudIcon,
  ExpandMore as ExpandMoreIcon,
  Inventory as InventoryIcon,
} from "@mui/icons-material";
import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import DashboardLayout from "../components/DashboardLayout";
import { useAuth } from "../contexts/AuthContext";
import { usePermissions } from "../hooks/usePermissions";
import { deploymentApi, usageApi } from "../services/api";
import { toast } from "react-toastify";
import { useTheme } from "@mui/material/styles";
import { useTranslation } from "react-i18next";

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
);

// eslint-disable-next-line no-unused-vars
const StatCardSkeleton = () => (
  <Card>
    <CardContent>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Box>
          <Skeleton variant="text" width={120} height={30} />
          <Skeleton variant="text" width={80} height={40} />
        </Box>
        <Skeleton variant="circular" width={40} height={40} />
      </Box>
    </CardContent>
  </Card>
);

const ChartSkeleton = () => (
  <Paper sx={{ p: 3 }}>
    <Skeleton variant="text" width={200} height={30} />
    <Divider sx={{ mb: 3 }} />
    <Box sx={{ height: 300 }}>
      <Skeleton variant="rectangular" height={300} />
    </Box>
  </Paper>
);

const DeploymentCardSkeleton = () => (
  <Card>
    <CardContent>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
        }}
      >
        <Skeleton variant="text" width="60%" height={30} />
        <Skeleton variant="rounded" width={80} height={24} />
      </Box>

      <Skeleton variant="text" width="40%" height={20} />

      <Box sx={{ display: "flex", justifyContent: "space-between", mt: 2 }}>
        <Skeleton variant="text" width="30%" height={20} />
        <Skeleton variant="text" width="20%" height={20} />
      </Box>
    </CardContent>
  </Card>
);

const Dashboard = () => {
  const { currentUser, userProfile } = useAuth();
  const { canViewDeployments } = usePermissions();
  const navigate = useNavigate();
  const theme = useTheme();
  const { t, i18n } = useTranslation();

  const [deployments, setDeployments] = useState([]);
  const [usage, setUsage] = useState(null);
  const [deploymentsLoading, setDeploymentsLoading] = useState(true);
  const [usageLoading, setUsageLoading] = useState(true);
  const [activeDeploymentsCount, setActiveDeploymentsCount] = useState(0);
  const [deployMenuAnchor, setDeployMenuAnchor] = useState(null);
  const deployMenuOpen = Boolean(deployMenuAnchor);

  useEffect(() => {
    // Fetch deployments data
    const fetchDeployments = async () => {
      if (!canViewDeployments) {
        setDeploymentsLoading(false);
        return;
      }

      try {
        const deploymentsResponse = await deploymentApi.listDeployments();
        const deploymentData = deploymentsResponse.data;
        setDeployments(deploymentData);

        // Count active deployments
        const activeCount = deploymentData.filter(
          (deployment) => deployment.is_active === true,
        ).length;
        setActiveDeploymentsCount(activeCount);
      } catch (error) {
        console.error("Error fetching deployments:", error);
        toast.error(t("toasts.failedToLoadDeploymentsData"));
      } finally {
        setDeploymentsLoading(false);
      }
    };

    // Fetch usage data
    const fetchUsage = async () => {
      if (!currentUser) return;

      try {
        // Get current usage summary
        const currentUsageResponse = await usageApi.getCurrentUsageSummary(
          currentUser.uid,
          currentUser.email,
          userProfile?.projectIds, // If projectIds are available in the userProfile
        );

        // Get historical usage summary for chart data
        // Set startDate to beginning of the current month
        const startDate = new Date();
        startDate.setDate(1); // First day of the current month
        startDate.setHours(0, 0, 0, 0); // Start of the day

        // Set endDate to current date/time
        const endDate = new Date();

        const usageSummaryResponse = await usageApi.getUsageSummary({
          userId: currentUser.uid,
          userEmail: currentUser.email,
          projectIds: userProfile?.projectIds, // If projectIds are available in the userProfile
          startDate: startDate,
          endDate: endDate,
        });

        // Combine the data
        const usageData = {
          ...usageSummaryResponse.data,
          current: currentUsageResponse.data,
        };

        setUsage(usageData);
      } catch (error) {
        console.error("Error fetching usage data:", error);
        toast.error(t("toasts.failedToLoadUsageData"));
      } finally {
        setUsageLoading(false);
      }
    };

    // Start both fetches in parallel
    fetchDeployments();
    fetchUsage();
  }, [currentUser, userProfile, canViewDeployments, t]);

  // Usage chart data
  const usageChartData = {
    labels:
      usage?.daily_usage?.map((day) =>
        new Date(day.date).toLocaleDateString(i18n.resolvedLanguage),
      ) || [],
    datasets: [
      {
        label: t("dashboard.gpuHours"),
        data: usage?.daily_usage?.map((day) => day.gpu_hours) || [],
        borderColor: "var(--color-68)",
        backgroundColor: "rgba(99, 102, 241, 0.1)",
        tension: 0.4,
        fill: true,
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: "top",
      },
      title: {
        display: false,
      },
    },
    scales: {
      y: {
        beginAtZero: true,
      },
    },
  };

  // Stats cards data
  const statsCards = [
    {
      title: t("dashboard.activeDeployments"),
      value: activeDeploymentsCount,
      icon: <CloudIcon fontSize="large" color="primary" />,
      color: "primary.main",
      loading: deploymentsLoading,
      deploymentRelated: true,
    },
    {
      title: t("dashboard.totalGpuHours"),
      value: usage?.total_gpu_hours?.toFixed(2) || "0.00",
      icon: <MemoryIcon fontSize="large" color="secondary" />,
      color: "secondary.main",
      loading: usageLoading,
    },
    {
      title: t("dashboard.currentCost"),
      value: `$${usage?.current?.total_cost?.toFixed(2) || "0.00"}`,
      icon: <BoltIcon fontSize="large" sx={{ color: "var(--color-69)" }} />,
      color: "var(--color-69)",
      loading: usageLoading,
    },
    {
      title: t("dashboard.activeSessions"),
      value: usage?.current?.active_sessions || "0",
      icon: <SpeedIcon fontSize="large" sx={{ color: "var(--color-70)" }} />,
      color: "var(--color-70)",
      loading: usageLoading,
    },
  ];

  const normalizeStatus = (status) => status?.toString().trim().toLowerCase();

  // Get deployment status color
  const getStatusColor = (status) => {
    switch (normalizeStatus(status)) {
      case "running":
        return "success";
      case "pending":
      case "starting":
        return "warning";
      case "failed":
        return "error";
      default:
        return "default";
    }
  };

  const getStatusLabel = (status) => {
    const normalizedStatus = normalizeStatus(status);

    if (
      [
        "archived",
        "failed",
        "paused",
        "pending",
        "running",
        "starting",
        "stopped",
        "success",
      ].includes(normalizedStatus)
    ) {
      return t(`deployments.${normalizedStatus}`);
    }

    return status || t("common.unknown");
  };

  // StatCard component with loading state
  const StatCard = ({ title, value, icon, color, loading }) => (
    <Card>
      <CardContent>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Box>
            <Typography variant="h6" component="div" gutterBottom>
              {title}
            </Typography>
            {loading ? (
              <Skeleton variant="text" width={80} height={40} />
            ) : (
              <Typography variant="h4" component="div" sx={{ color }}>
                {value}
              </Typography>
            )}
          </Box>
          <Box sx={{ color }}>{icon}</Box>
        </Box>
      </CardContent>
    </Card>
  );

  const handleDeployMenuClick = (event) => {
    setDeployMenuAnchor(event.currentTarget);
  };

  const handleDeployMenuClose = () => {
    setDeployMenuAnchor(null);
  };

  const handleDeployOptionClick = (path) => {
    navigate(path);
    handleDeployMenuClose();
  };

  return (
    <DashboardLayout title={t("dashboard.title")}>
      <Box sx={{ mb: 4 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={8}>
            <Typography variant="h4" component="h1" gutterBottom>
              {t("dashboard.welcome")}{" "}
              {userProfile?.displayName ||
                currentUser?.email?.split("@")[0] ||
                t("common.user")}
            </Typography>
            <Typography variant="body1" color="text.secondary">
              {t("dashboard.subtitle")}
            </Typography>
          </Grid>
          <Grid
            item
            xs={12}
            sm={4}
            sx={{
              display: "flex",
              justifyContent: { xs: "flex-start", sm: "flex-end" },
            }}
          >
            {/* <Button
              variant="contained"
              endIcon={<ExpandMoreIcon />}
              onClick={handleDeployMenuClick}
              sx={{
                minWidth: { xs: "100%", sm: "100%", md: "auto", lg: "auto" },
              }}
            >
              Deploy Model
            </Button> */}
            <Menu
              id="deploy-menu"
              anchorEl={deployMenuAnchor}
              open={deployMenuOpen}
              onClose={handleDeployMenuClose}
              anchorOrigin={{
                vertical: "bottom",
                horizontal: "center",
              }}
              transformOrigin={{
                vertical: "top",
                horizontal: "center",
              }}
              sx={{
                "& .MuiPaper-root": {
                  mt: 0.5,
                  borderRadius: 1.5,
                  minWidth: 200,
                  boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.15)",
                  backgroundColor:
                    theme.palette.mode === "light"
                      ? "var(--color-3)"
                      : "var(--color-29)",
                  "& .MuiList-root": {
                    padding: "8px 0",
                  },
                },
              }}
            >
              <MenuItem
                onClick={() => handleDeployOptionClick("/predefined-models")}
                sx={{
                  py: 1.5,
                  px: 2,
                  "&:hover": {
                    backgroundColor:
                      theme.palette.mode === "light"
                        ? "rgba(0, 0, 0, 0.04)"
                        : "rgba(255, 255, 255, 0.1)",
                  },
                }}
              >
                <Box
                  component="span"
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                  }}
                >
                  <AddIcon fontSize="small" />
                  {t("dashboard.preDefinedModels")}
                </Box>
              </MenuItem>
              <MenuItem
                onClick={() => handleDeployOptionClick("/create-deployment")}
                sx={{
                  py: 1.5,
                  px: 2,
                  "&:hover": {
                    backgroundColor:
                      theme.palette.mode === "light"
                        ? "rgba(0, 0, 0, 0.04)"
                        : "rgba(255, 255, 255, 0.1)",
                  },
                }}
              >
                <Box
                  component="span"
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                  }}
                >
                  <InventoryIcon fontSize="small" />
                  {t("dashboard.customDeployments")}
                </Box>
              </MenuItem>
            </Menu>
          </Grid>
        </Grid>
      </Box>

      {/* Stats Cards - each card shows its own loading state */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {statsCards
          .filter((card) => canViewDeployments || !card.deploymentRelated)
          .map((card, index) => (
            <Grid item xs={12} sm={6} md={3} key={index}>
              <StatCard
                title={card.title}
                value={card.value}
                icon={card.icon}
                color={card.color}
                loading={card.loading}
              />
            </Grid>
          ))}
      </Grid>

      {/* Usage Chart */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12}>
          {usageLoading ? (
            <ChartSkeleton />
          ) : (
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                {t("dashboard.usageOverTime")}
              </Typography>
              <Divider sx={{ mb: 3 }} />
              {usage?.daily_usage?.length > 0 ? (
                <Box sx={{ height: 300 }}>
                  <Line data={usageChartData} options={chartOptions} />
                </Box>
              ) : (
                <Box sx={{ py: 4, textAlign: "center" }}>
                  <Typography color="text.secondary">
                    {t("dashboard.noUsageData")}
                  </Typography>
                </Box>
              )}
            </Paper>
          )}
        </Grid>
      </Grid>

      {canViewDeployments && (
        <>
          {/* Recent Deployments */}
          <Box sx={{ mb: 2 }}>
            <Typography variant="h6" gutterBottom>
              {t("dashboard.recentDeployments")}
            </Typography>
          </Box>

      <Grid container spacing={3}>
        {deploymentsLoading ? (
          // Show skeleton cards while loading
          Array.from({ length: 4 }).map((_, index) => (
            <Grid item xs={12} md={6} key={`skeleton-${index}`}>
              <DeploymentCardSkeleton />
            </Grid>
          ))
        ) : deployments.length > 0 ? (
          deployments.slice(0, 4).map((deployment) => (
            <Grid item xs={12} md={6} key={deployment.model_id}>
              <Card
                sx={{
                  cursor: "pointer",
                  transition: "transform 0.2s",
                  "&:hover": {
                    transform: "translateY(-4px)",
                    boxShadow: "0 8px 24px rgba(0, 0, 0, 0.15)",
                  },
                }}
                onClick={() => navigate(`/deployments/${deployment.model_id}`)}
              >
                <CardContent>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      mb: 2,
                    }}
                  >
                    <Typography variant="h6" component="div">
                      {deployment.name}
                    </Typography>
                    <Chip
                      label={getStatusLabel(deployment.status)}
                      size="small"
                      color={getStatusColor(deployment.status)}
                    />
                  </Box>

                  <Typography
                    variant="body2"
                    color="text.secondary"
                    gutterBottom
                  >
                    {t("deploymentDetail.modelId")}: {deployment.model_id}
                  </Typography>

                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      mt: 2,
                    }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      {t("common.created")}:{" "}
                      {new Date(deployment.created_at).toLocaleDateString(
                        i18n.resolvedLanguage,
                      )}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {t("common.version")}: {deployment.version}
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))
        ) : (
          <Grid item xs={12}>
            <Paper sx={{ p: 4, textAlign: "center" }}>
              <Typography color="text.secondary" paragraph>
                {t("dashboard.noDeployments")}
              </Typography>
              <Button
                variant="contained"
                endIcon={<ExpandMoreIcon />}
                onClick={handleDeployMenuClick}
              >
                {t("dashboard.deployFirstModel")}
              </Button>
            </Paper>
          </Grid>
        )}
      </Grid>
        </>
      )}
    </DashboardLayout>
  );
};

export default Dashboard;
